import { useEffect, useRef, useState } from 'preact/hooks';
import { startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { CaptureGlyph as Knot } from '../components/CaptureGlyph';
import {
  getFirstRun,
  completeFirstRunStep,
  passkeyRegisterOptions,
  passkeyRegisterVerify,
  uploadRecordBlob,
  addMyTicket,
  createMyRecord,
  enrolMemberTelegram,
  knotIntentCreate,
  fileAskFromOffer,
  authorityOfferFrom,
  ApiError,
  type ApiUser,
  type FirstRunState,
  type FirstRunTicket,
} from '../lib/api';
import { useKnotPushToTalk } from '../lib/useKnotPushToTalk';
import { enablePush, readPushState, isIOS, isStandalone, type PushState } from '../lib/pushEnrolment';
import './FirstRun.css';

export interface FirstRunProps {
  tenantId: string;
  user: ApiUser;
  /** Called the moment the walk is complete (or was already complete) —
   * App drops into the ordinary shell. */
  onDone: () => void;
}

// C6b (wf05 §02, blessed 2026-08-06): a tech's first run — four screens,
// and the last one is doing the job. The SCRIPT comes from the server
// (GET /api/first-run — the pack declares it; this component renders
// whatever subset arrives, in order). Three rules carried structurally:
//
//   1. Everything is skippable except identity ("Do this later" — pure
//      navigation, NEVER carrying a disabled predicate: the C6a
//      invariant, re-asserted here in the framework, so a WebAuthn
//      ceremony or a poll that never resolves cannot close the exit).
//   2. Completion state is SERVER-SIDE (the 08-04 SW incident): every
//      step passage is a POST, re-entry resumes at the first incomplete
//      step, and a completed first-run never re-enters — App asks the
//      server, not a cache.
//   3. No instructional captions. The tutorial IS a real first job: the
//      last step drives the PRODUCTION Knot door and shows the receipt.
//
// C7 — AND THE FOUNDER IS A PERSON TOO. C6b was walked as Tim, the invited
// tech: Wendy typed his phone (so an enrolment existed to match) and the
// office bound his profile at invite time (so tickets had somewhere to
// land). Alan, who CREATES the tenant, is preceded by nobody, and each of
// the three screens below gained the half that assumed an admin had gone
// first: the lanes step can enrol the caller's OWN phone (when the
// MANIFEST says they may), the tickets step says out loud why it is empty
// and can make the caller's own file, and the capture step's "hold the
// gold knot" is finally a hold — wired to the real mic, degrading its own
// COPY when there is no mic to hold.
type Phase =
  | { kind: 'loading' }
  | { kind: 'step'; stepIndex: number }
  | { kind: 'done' };

function firstName(state: FirstRunState | null, user: ApiUser): string {
  const source = state?.identity.name ?? user.name ?? user.email.split('@')[0] ?? '';
  const first = source.trim().split(/\s+/)[0];
  return first || source;
}

const POLL_MS = 2500;

/** Chromium's install event — untyped in lib.dom, minimally typed here. */
interface BeforeInstallPromptLike extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// `isStandalone` and `isIOS` were declared HERE in CV2 and moved to
// lib/pushEnrolment in CV6b — not copied. The push step and the install step
// ask the same two questions of the same device, and two implementations of
// "am I installed" that could ever disagree is exactly the class of bug that
// makes a phone silently stop buzzing. (iOS has no install event at all,
// which is why install_app's invitation is a drawing; iPadOS 13+ reports
// MacIntel with touch, which is that function's second clause.)

export function FirstRun({ tenantId, user, onDone }: FirstRunProps) {
  const [state, setState] = useState<FirstRunState | null>(null);
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // identity
  const [name, setName] = useState('');
  // chat lanes — C7: the caller's own enrolment, when they may make one
  const [phone, setPhone] = useState('');
  // tickets
  const [ticketName, setTicketName] = useState('');
  const [ticketExpiry, setTicketExpiry] = useState('');
  const [tickets, setTickets] = useState<FirstRunTicket[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  // first capture
  const [captureText, setCaptureText] = useState('');
  const [receipt, setReceipt] = useState<{ text: string; line: string } | null>(null);
  const [askOffer, setAskOffer] = useState<{ token: string | null; filed: boolean } | null>(null);

  useEffect(() => {
    let cancelled = false;
    getFirstRun(tenantId)
      .then((s) => {
        if (cancelled) return;
        setState(s);
        setName(s.identity.name ?? user.name ?? '');
        setTickets(s.tickets?.items ?? []);
        if (s.completedAt || s.resume === null) {
          setPhase({ kind: 'done' });
          onDone();
        } else {
          setPhase({ kind: 'step', stepIndex: s.steps.indexOf(s.resume) });
        }
      })
      .catch(() => {
        // The walk must never trap anyone the server can't answer for —
        // fail OPEN into the ordinary shell.
        if (!cancelled) onDone();
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const stepId =
    phase.kind === 'step' && state ? state.steps[phase.stepIndex] ?? null : null;

  // The lane rows breathe: while the chat_lanes step is on screen, the
  // enrolment state is re-read on a short interval so a bind performed on
  // the phone flips the rows without any tap here.
  useEffect(() => {
    if (stepId !== 'chat_lanes') return;
    const timer = setInterval(() => {
      getFirstRun(tenantId)
        .then((s) => setState((prev) => (prev ? { ...prev, lanes: s.lanes } : s)))
        .catch(() => {
          /* a missed poll is just the next poll's problem */
        });
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [stepId, tenantId]);

  /* ---------------------------------------------------------------- *
   * C7 — THE HOLD. "Hold the gold knot and tell me what you did today"
   * only ever selected text: the copy promised a gesture the screen did
   * not carry. This is the app's own push-to-talk hook (lib/
   * useKnotPushToTalk), driven with its transcript short-circuit — the
   * step's target is PACK-DECLARED, so the utterance goes to the SAME
   * production create the typed box uses, never through a classifier
   * that could file it somewhere else. Whatever was heard is put in the
   * textarea first, so a failed create restates it rather than losing it.
   *
   * Called ABOVE the early returns below: hooks are unconditional, and
   * the capture handler it needs is reached through a ref (assigned at
   * the bottom of this component) rather than by hoisting the whole
   * capture step up here.
   * ---------------------------------------------------------------- */
  const captureVoiceRef = useRef<(text: string) => void>(() => {});
  const ptt = useKnotPushToTalk(
    tenantId,
    null,
    // A quick tap needs no sheet here — the typed box is already on
    // screen, which is the whole shape of this step.
    () => {},
    (transcript: string) => setCaptureText(transcript),
    undefined,
    (transcript: string) => captureVoiceRef.current(transcript)
  );

  /* ---------------------------------------------------------------- *
   * CV2 — ADD TO HOME SCREEN ('install_app'). Three hooks, all above
   * the early returns (rules of hooks):
   *
   *   1. `beforeinstallprompt` is CAPTURED FROM MOUNT, not from the step
   *      — Chromium fires it early, usually before the walk reaches this
   *      screen, and nothing captured it before CV2 (verified: no other
   *      listener in the package), so the real install button had no
   *      event to spend.
   *   2. Already standalone → the step AUTO-PASSES silently: never ask
   *      someone to install what they are inside of. Single-flight rides
   *      passStep's own `passing` ref.
   * ---------------------------------------------------------------- */
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptLike | null>(null);
  useEffect(() => {
    function onPrompt(e: Event) {
      // Chromium's own mini-infobar is suppressed so the step can offer
      // the install at ITS moment, with the walk's voice.
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptLike);
    }
    window.addEventListener('beforeinstallprompt', onPrompt);
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);
  useEffect(() => {
    if (stepId === 'install_app' && isStandalone()) void passStep('install_app');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepId]);

  /* ---------------------------------------------------------------- *
   * CV6b §3 — GET PINGED ABOUT YOUR JOBS ('push_pings').
   *
   * The step renders the STATE lib/pushEnrolment reports and never guesses:
   * on iOS before the install it says what to do first rather than offering
   * a button that cannot work; denied says plainly that only device settings
   * can undo it; an unconfigured deployment says the deployment has no keys.
   *
   * The state is read when the step ARRIVES, not at mount — reading it costs
   * a request (the VAPID key) and a walk that skips the step should never
   * spend it. Already granted AND subscribed auto-passes, silently: never
   * ask someone for something they have already given.
   * ---------------------------------------------------------------- */
  const [pushState, setPushState] = useState<PushState | null>(null);
  const [pushError, setPushError] = useState<string | null>(null);
  useEffect(() => {
    if (stepId !== 'push_pings') return;
    let cancelled = false;
    readPushState(tenantId)
      .then((status) => {
        if (cancelled) return;
        if (status.state === 'on') {
          void passStep('push_pings');
          return;
        }
        setPushState(status.state);
      })
      .catch(() => {
        // A state we could not read is not a state we may assert. 'off'
        // renders the ordinary button, and the attempt itself will say what
        // actually happened.
        if (!cancelled) setPushState('off');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepId, tenantId]);

  if (phase.kind === 'loading' || phase.kind === 'done' || !state || !stepId) {
    return (
      <div class="frun">
        <div class="frun__center">
          <Knot size={46} />
        </div>
      </div>
    );
  }

  const stepIndex = phase.stepIndex;

  function advance(next: { stepsDone: string[]; completedAt: string | null; resume: string | null }) {
    setState((prev) =>
      prev ? { ...prev, stepsDone: next.stepsDone, completedAt: next.completedAt } : prev
    );
    setError(null);
    setBusy(false);
    if (next.completedAt || next.resume === null) {
      setPhase({ kind: 'done' });
      onDone();
    } else {
      setPhase({ kind: 'step', stepIndex: state!.steps.indexOf(next.resume) });
    }
  }

  /** Marks the current step passed. Single-flight lives HERE, in the
   * handler (a ref, deliberately NOT the `busy` state) — never as a
   * disabled= on a skip control, and never sharing the primary button's
   * busy flag: a passkey ceremony that hangs forever leaves `busy` true,
   * and the way OUT must still work (the C6a invariant, structurally). */
  const passing = useRef(false);
  async function passStep(id: string, body?: { name?: string }) {
    if (passing.current) return;
    passing.current = true;
    setError(null);
    try {
      advance(await completeFirstRunStep(tenantId, id, body));
    } catch {
      setError('That didn’t go through. Try again.');
    }
    passing.current = false;
  }

  const bars = (
    <div class="frun__bars" aria-hidden="true">
      {state.steps.map((s, i) => (
        <i key={s} class={`frun__bar${i <= stepIndex ? ' frun__bar--on' : ''}`} />
      ))}
    </div>
  );

  const errorLine = error ? (
    <div class="frun__error" role="alert">
      {error}
    </div>
  ) : null;

  /* ---------------------------------------------------------------- *
   * 1 · who you are — the ONE unskippable step.
   * ---------------------------------------------------------------- */
  if (stepId === 'identity') {
    const setUpBy = state.identity.setUpByName;
    const canCeremony = browserSupportsWebAuthn();

    async function confirmIdentity(withPasskey: boolean) {
      const trimmed = name.trim();
      if (!trimmed) {
        setError('Your name is needed.');
        return;
      }
      if (busy) return;
      if (withPasskey) {
        setBusy(true);
        setError(null);
        try {
          const { ceremonyId, options } = await passkeyRegisterOptions();
          const response = await startRegistration({
            optionsJSON: options as Parameters<typeof startRegistration>[0]['optionsJSON'],
          });
          await passkeyRegisterVerify(ceremonyId, response);
        } catch {
          // A declined/failed ceremony must never block the walk — the
          // step still completes; sign-in keeps its other doors.
        }
        setBusy(false);
      }
      await passStep('identity', { name: trimmed });
    }

    return (
      <div class="frun">
        {bars}
        <div class="frun__head">
          <Knot size={40} />
          <h2 class="frun__say">
            {setUpBy ? `You’re ${firstName(state, user)}, and ${setUpBy} set this up for you.` : `You’re ${firstName(state, user)}.`}
          </h2>
          <div class="frun__sub">Two minutes, then you’re done.</div>
        </div>
        {errorLine}
        <label class="frun__label" for="frun-name">
          Your name
        </label>
        <input
          id="frun-name"
          class="frun__input"
          type="text"
          autocomplete="name"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
        />
        {state.identity.phoneMasked ? (
          <>
            <label class="frun__label" for="frun-mobile">
              Mobile
            </label>
            <input id="frun-mobile" class="frun__input" type="text" value={state.identity.phoneMasked} disabled />
            <div class="frun__hint">
              {setUpBy ? `From the office. Tell ${setUpBy} if it’s wrong.` : 'From the office.'}
            </div>
          </>
        ) : null}
        <div class="frun__grow" />
        {canCeremony ? (
          <>
            <button type="button" class="frun__btn" disabled={busy} onClick={() => void confirmIdentity(true)}>
              {busy ? 'Setting up…' : 'Set up Face / Touch ID'}
            </button>
            {/* wf05's own words. NO disabled= ever (C6a invariant): a
                ceremony that hangs leaves `busy` true, and this door must
                stay open — it completes the step WITHOUT the passkey
                (identity itself is still confirmed: the name is required
                here exactly as on the primary path). */}
            <button
              type="button"
              class="frun__textlink"
              onClick={() => {
                const trimmed = name.trim();
                if (!trimmed) {
                  setError('Your name is needed.');
                  return;
                }
                void passStep('identity', { name: trimmed });
              }}
            >
              Use a password instead
            </button>
          </>
        ) : (
          <button type="button" class="frun__btn" disabled={busy} onClick={() => void confirmIdentity(false)}>
            Continue
          </button>
        )}
      </div>
    );
  }

  /* ---------------------------------------------------------------- *
   * 1b · CV2 — the home screen. Auto-passes under standalone (the effect
   * above; this branch then only shows the quiet knot for a beat). On
   * Chromium the captured beforeinstallprompt makes the button REAL; on
   * iOS there is no API, so the invitation is a drawing — the one place
   * the no-instructional-captions doctrine deliberately bends, kept
   * closer to a drawing than a manual. "Not now" always proceeds: a
   * first run never hard-blocks on a phone setting.
   * ---------------------------------------------------------------- */
  if (stepId === 'install_app') {
    if (isStandalone()) {
      return (
        <div class="frun">
          <div class="frun__center">
            <Knot size={46} />
          </div>
        </div>
      );
    }

    async function offerInstall() {
      if (!installPrompt || busy) return;
      setBusy(true);
      setError(null);
      try {
        await installPrompt.prompt();
        const choice = await installPrompt.userChoice;
        if (choice.outcome === 'accepted') {
          await passStep('install_app');
          return;
        }
        // Dismissed the browser's sheet — not a failure, and not a pass:
        // the step stays, and "Not now" remains the way past it.
      } catch {
        // A spent or refused prompt event; the buttons below still work.
      }
      setBusy(false);
    }

    return (
      <div class="frun">
        {bars}
        <div class="frun__head">
          <h2 class="frun__say">Keep it on your home screen</h2>
          {/* The one honest line about why it matters — a fact, not a pitch. */}
          <div class="frun__sub">
            Installed, the microphone stays allowed and the phone can tell you when something
            needs you.
          </div>
        </div>
        {errorLine}
        {isIOS() && !installPrompt ? (
          // The drawing: Safari's share glyph, then the row you're looking
          // for. Two lines of copy at most — the glyphs do the pointing.
          <div class="frun__install-draw" data-testid="frun-install-ios">
            <span class="frun__install-glyph" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="26" height="26">
                <g fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 14V4" />
                  <path d="M8.5 7.5 12 4l3.5 3.5" />
                  <path d="M7 10H5.5A1.5 1.5 0 0 0 4 11.5v7A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5v-7A1.5 1.5 0 0 0 18.5 10H17" />
                </g>
              </svg>
            </span>
            <span class="frun__install-arrow" aria-hidden="true">
              →
            </span>
            <span class="frun__install-row">
              Add to Home Screen
              <b aria-hidden="true">＋</b>
            </span>
          </div>
        ) : null}
        <div class="frun__grow" />
        {installPrompt ? (
          <button type="button" class="frun__btn" disabled={busy} onClick={() => void offerInstall()}>
            {busy ? 'One moment…' : 'Add to Home Screen'}
          </button>
        ) : (
          <button type="button" class="frun__btn" disabled={busy} onClick={() => void passStep('install_app')}>
            I added it
          </button>
        )}
        {/* Skippable, always (C6a invariant: no disabled=, ever). */}
        <button type="button" class="frun__textlink" onClick={() => void passStep('install_app')}>
          Not now
        </button>
      </div>
    );
  }

  /* ---------------------------------------------------------------- *
   * 1c · CV6b — the phone tells you. Same posture as install_app: auto-
   * passes when it is already on (the effect above), "Not now" always
   * proceeds, and NO disabled= on the way out (the C6a invariant).
   *
   * The one rule this screen exists to keep: A DEAD BUTTON IS WORSE THAN NO
   * BUTTON. Four of the six states cannot produce a subscription, and each
   * of them says what is true instead of offering a control that fails
   * silently — because a silent failure here is a phone that never buzzes
   * and a person who believes it will.
   * ---------------------------------------------------------------- */
  if (stepId === 'push_pings') {
    const state = pushState;
    if (state === null) {
      // THE BEAT KEEPS ITS ESCAPE. Reading the state touches the service
      // worker and the network, and `navigator.serviceWorker.ready` NEVER
      // settles when no worker registers — lib/pushEnrolment bounds that
      // wait, and this renders the way out while it happens, because a
      // spinner with no exit is exactly the trap the C6a invariant forbids.
      return (
        <div class="frun">
          {bars}
          <div class="frun__center">
            <Knot size={46} />
          </div>
          <div class="frun__grow" />
          <button type="button" class="frun__textlink" onClick={() => void passStep('push_pings')}>
            Not now
          </button>
        </div>
      );
    }

    async function turnOnPings() {
      if (busy) return;
      setBusy(true);
      setPushError(null);
      const status = await enablePush(tenantId);
      setPushState(status.state);
      setPushError(status.error ?? null);
      setBusy(false);
      if (status.state === 'on') await passStep('push_pings');
    }

    // What each state SAYS, in the walk's voice. No instructional captions:
    // these are statements of fact about this device, which is the one thing
    // the screen can honestly offer when it cannot offer a button.
    const say: Record<PushState, string> = {
      on: 'Your phone will tell you.',
      off: 'Get pinged about your jobs.',
      // iOS before the install: the fix is one step back, and CV2 built it.
      'needs-install': 'Add Rexx to your home screen first, then this can turn on.',
      blocked: 'Notifications are switched off for Rexx in your phone’s settings.',
      unsupported: 'This browser can’t do notifications.',
      unconfigured: 'Notifications aren’t set up on this Rexx yet.',
    };

    return (
      <div class="frun">
        {bars}
        <div class="frun__head">
          <h2 class="frun__say">{say[state]}</h2>
          {state === 'off' || state === 'on' ? (
            // The one honest line about what arrives — a fact, not a pitch,
            // and the same register install_app's own sub-line uses.
            <div class="frun__sub">
              A job lands on you, the office writes — the phone says so without you looking.
            </div>
          ) : null}
        </div>
        {errorLine}
        {pushError ? (
          <div class="frun__error" role="alert" data-testid="frun-push-error">
            {pushError}
          </div>
        ) : null}
        {/* Decision 0d25040b ruling 3 — "the Telegram double-ping is SAID,
            not built" — deliberately does NOT live here. Turning pings on
            passes the step in the same beat, so this screen's 'on' state is
            never a page anybody reads; a line placed here would have been
            written, shipped, and seen by nobody. It lives in Settings ›
            Notifications, which renders 'on' durably. Found by a test that
            asserted the line rather than trusting it rendered. */}
        {state === 'blocked' ? (
          // THE WEB CANNOT RE-ASK. Saying where the switch is is the only
          // thing left that is true and useful.
          <p class="frun__fine" data-testid="frun-push-blocked">
            Settings › Notifications › Rexx. Nothing here can turn them back on.
          </p>
        ) : null}
        <div class="frun__grow" />
        {state === 'off' ? (
          <button
            type="button"
            class="frun__btn"
            disabled={busy}
            onClick={() => void turnOnPings()}
            data-testid="frun-push-on"
          >
            {busy ? 'One moment…' : 'Turn on notifications'}
          </button>
        ) : null}
        {/* Skippable, always (C6a invariant: no disabled=, ever). "Not now"
            when there was something to say no to; "Continue" when the state
            left nothing to decide — the same word doing a different job
            would be the lie. */}
        <button type="button" class="frun__textlink" onClick={() => void passStep('push_pings')}>
          {state === 'off' ? 'Not now' : 'Continue'}
        </button>
      </div>
    );
  }

  /* ---------------------------------------------------------------- *
   * 2 · the two lanes.
   * ---------------------------------------------------------------- */
  if (stepId === 'chat_lanes') {
    const connected = state.lanes.length > 0 && state.lanes.every((l) => l.connected);
    // C7: the enrolment door — the caller may enrol (the manifest said so)
    // and no number is on file yet. Once one is, the rows below ARE the
    // state, and there is nothing left to type.
    const selfEnrol = state.selfEnrol;
    const offerEnrol = Boolean(selfEnrol?.available) && !selfEnrol!.phoneMasked;

    async function sendEnrolment() {
      const typed = phone.trim();
      if (!typed || busy || !selfEnrol) return;
      setBusy(true);
      setError(null);
      try {
        await enrolMemberTelegram(tenantId, selfEnrol.membershipId, typed);
        // Re-read rather than patch: the enrolment's masked number and the
        // lane rows are the SERVER's account of what just happened.
        setState(await getFirstRun(tenantId));
        setPhone('');
      } catch (err) {
        // The route's own words when it refuses the NUMBER (E.164 is
        // required — a guessed country code would be an invented phone).
        // Swallowing that would leave someone retyping the same thing.
        setError(
          err instanceof ApiError && err.status === 400 ? err.message : 'That didn’t go through. Try again.'
        );
      }
      setBusy(false);
    }

    return (
      <div class="frun">
        {bars}
        <div class="frun__head">
          <h2 class="frun__say">Connect Telegram</h2>
          <div class="frun__sub">Job chats and messages from the office come through here.</div>
        </div>
        {errorLine}
        <div class="frun__lanes">
          {state.lanes.map((lane) => (
            <div key={lane.key} class="frun__lane">
              <span class={`frun__lane-mark${lane.connected ? ' frun__lane-mark--on' : ''}`} aria-hidden="true">
                {lane.connected ? '✓' : '○'}
              </span>
              <span class="frun__lane-label">
                <b>{lane.label}</b> — {lane.connected ? 'connected' : 'waiting'}
              </span>
              {!lane.connected && lane.deepLink ? (
                <a class="frun__lane-open" href={lane.deepLink} target="_blank" rel="noreferrer">
                  Open
                </a>
              ) : null}
            </div>
          ))}
        </div>
        {offerEnrol ? (
          <div class="frun__enrol">
            <label class="frun__label" for="frun-phone">
              Your mobile
            </label>
            <input
              id="frun-phone"
              class="frun__input"
              type="tel"
              autocomplete="tel"
              value={phone}
              onInput={(e) => setPhone((e.target as HTMLInputElement).value)}
            />
            <button
              type="button"
              class="frun__btn frun__btn--ghost"
              disabled={busy || !phone.trim()}
              onClick={() => void sendEnrolment()}
            >
              {/* NOT "Send me the link": this route SENDS NOTHING — it
                  registers the number so a later contact-share can bind it.
                  Receipts-not-acknowledgements: a button must name what it
                  actually does (executor's objection at C7 review, upheld). */}
              {busy ? 'One moment…' : 'Use this number'}
            </button>
          </div>
        ) : null}
        <div class="frun__grow" />
        <p class="frun__fine">
          Telegram will ask you to share your number. That is how we know it’s you and not someone
          else joining the job chats.
        </p>
        {connected ? (
          <button type="button" class="frun__btn" disabled={busy} onClick={() => void passStep('chat_lanes')}>
            Continue
          </button>
        ) : (
          <button type="button" class="frun__btn" disabled>
            Waiting for Telegram…
          </button>
        )}
        {/* Skippable (C6a invariant: no disabled=, ever). */}
        <button type="button" class="frun__textlink" onClick={() => void passStep('chat_lanes')}>
          Do this later
        </button>
      </div>
    );
  }

  /* ---------------------------------------------------------------- *
   * 3 · what lets you work.
   * ---------------------------------------------------------------- */
  if (stepId === 'tickets') {
    const available = state.tickets?.available ?? false;
    const canCreateProfile = state.tickets?.canCreateProfile ?? false;

    /** C7 §1b — the caller makes their OWN file. The server writes the
     * name it already holds; nothing is sent, and nothing is invented. */
    async function startMyFile() {
      if (busy) return;
      setBusy(true);
      setError(null);
      try {
        await createMyRecord(tenantId);
        // The step's whole state changes with the binding — re-read it.
        setState(await getFirstRun(tenantId));
      } catch {
        setError('That didn’t go through. Try again.');
      }
      setBusy(false);
    }

    async function photographTicket(file: File) {
      const trimmed = ticketName.trim();
      if (!trimmed) {
        setError('A name for the ticket is needed.');
        return;
      }
      if (busy) return;
      setBusy(true);
      setError(null);
      try {
        const { ref } = await uploadRecordBlob(tenantId, file);
        const result = await addMyTicket(tenantId, {
          name: trimmed,
          ...(ticketExpiry ? { expiresOn: ticketExpiry } : {}),
          fileRef: ref,
        });
        setTickets(result.tickets);
        setTicketName('');
        setTicketExpiry('');
      } catch {
        setError('That didn’t go through. Try again.');
      }
      setBusy(false);
    }

    return (
      <div class="frun">
        {bars}
        <div class="frun__head">
          <h2 class="frun__say">Your tickets</h2>
          <div class="frun__sub">Photograph them once. We’ll remind you before they expire.</div>
        </div>
        {errorLine}
        {tickets.length > 0 ? (
          <div class="frun__tickets">
            {tickets.map((t) => (
              <div key={t.id} class="frun__ticket">
                <span class="frun__ticket-name">
                  {t.name}
                  {t.expires_on ? <small>Expires {t.expires_on}</small> : null}
                </span>
                <span class="frun__ticket-mark">✓</span>
              </div>
            ))}
          </div>
        ) : null}
        {available ? (
          <>
            <label class="frun__label" for="frun-ticket-name">
              Ticket
            </label>
            <input
              id="frun-ticket-name"
              class="frun__input"
              type="text"
              value={ticketName}
              onInput={(e) => setTicketName((e.target as HTMLInputElement).value)}
            />
            <label class="frun__label" for="frun-ticket-expiry">
              Expires
            </label>
            <input
              id="frun-ticket-expiry"
              class="frun__input"
              type="date"
              value={ticketExpiry}
              onInput={(e) => setTicketExpiry((e.target as HTMLInputElement).value)}
            />
            <input
              ref={fileInput}
              class="frun__file"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (file) void photographTicket(file);
                (e.target as HTMLInputElement).value = '';
              }}
            />
            <div class="frun__grow" />
            <button type="button" class="frun__btn" disabled={busy} onClick={() => fileInput.current?.click()}>
              {busy ? 'One moment…' : 'Photograph a ticket'}
            </button>
          </>
        ) : (
          <>
            {/* SAY WHY. The step used to be skip-only with no account of
                itself — honest, and hollow. Tickets hang on a person's
                file; this account has none yet. Nobody should have to
                guess whether that is their fault. */}
            <p class="frun__fine">
              Tickets are kept on a person’s file. This account doesn’t have one yet.
            </p>
            <div class="frun__grow" />
            {canCreateProfile ? (
              <button type="button" class="frun__btn" disabled={busy} onClick={() => void startMyFile()}>
                {busy ? 'One moment…' : 'Start my file'}
              </button>
            ) : null}
          </>
        )}
        {tickets.length > 0 ? (
          <button type="button" class="frun__btn frun__btn--ghost" onClick={() => void passStep('tickets')}>
            Continue
          </button>
        ) : null}
        <button type="button" class="frun__textlink" onClick={() => void passStep('tickets')}>
          Do this later
        </button>
      </div>
    );
  }

  /* ---------------------------------------------------------------- *
   * 4 · the Knot, by using it — a REAL capture through the production
   * door, receipt on screen. Never explained, only used.
   * ---------------------------------------------------------------- */
  const target = state.firstCapture;

  /** C7 — CAN THIS DEVICE EVEN HOLD? No MediaRecorder / no getUserMedia
   * (desktop Safari's older builds, an insecure origin, a locked-down
   * webview), or the mic was refused at the prompt. The COPY reads this:
   * telling someone to hold a knot that cannot listen is an instruction
   * that can only be failed. */
  const holdSupported =
    typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
  const canHold = holdSupported && !ptt.micDenied;

  async function capture(spoken?: string) {
    if (!target || busy) return;
    const text = (spoken ?? captureText).trim();
    if (!text) return;
    setBusy(true);
    setError(null);
    try {
      const fields: Record<string, unknown> = target.textField ? { [target.textField]: text } : {};
      await knotIntentCreate(tenantId, target.targetKey, fields);
      setReceipt({ text, line: `${target.label.toLowerCase()} logged` });
      setAskOffer(null);
    } catch (err) {
      const offer = authorityOfferFrom(err);
      if (offer) {
        // §4's authority-409 shape: the C5e offer, with the one minimal
        // affordance — file the signed token, nothing else invented.
        setAskOffer({ token: offer.fileAsk?.offerToken ?? null, filed: false });
      } else {
        setError('That didn’t go through. Try again.');
      }
    }
    setBusy(false);
  }

  // The hold's landing point (assigned every render, read by the hook's
  // callback — the same ref pattern useKnotPushToTalk uses for its own
  // context). The transcript goes into the box FIRST: if the create then
  // refuses, what was said is on screen to edit, never lost.
  captureVoiceRef.current = (spoken: string) => {
    setCaptureText(spoken);
    void capture(spoken);
  };

  async function sendAsk() {
    if (!askOffer?.token || busy) return;
    setBusy(true);
    try {
      await fileAskFromOffer(tenantId, askOffer.token);
      setAskOffer({ token: askOffer.token, filed: true });
      setReceipt({ text: captureText.trim(), line: 'with the office' });
    } catch {
      setError('That didn’t go through. Try again.');
    }
    setBusy(false);
  }

  return (
    <div class="frun">
      {bars}
      <div class="frun__head">
        <h2 class="frun__say">One last thing.</h2>
        {/* The copy and the capability say the SAME thing. Where a hold
            can work it is named; where it cannot, the line asks for the
            typed sentence the box below already takes — never a promise
            of a gesture this device does not have. */}
        <div class="frun__sub">
          {canHold ? 'Hold the gold knot and tell me what you did today.' : 'Tell me what you did today.'}
        </div>
      </div>
      {errorLine}
      {ptt.error ? (
        <div class="frun__error" role="status" onClick={ptt.dismissError}>
          {ptt.error}
        </div>
      ) : null}
      <div class="frun__knot">
        {canHold ? (
          <button
            type="button"
            class="frun__knot-btn"
            aria-label="Capture"
            onClick={ptt.onClick}
            onPointerDown={ptt.onPointerDown}
            onPointerUp={ptt.onPointerUp}
            onPointerCancel={ptt.onPointerCancel}
            onLostPointerCapture={ptt.onLostPointerCapture}
          >
            <Knot size={64} amp={ptt.amp} state={receipt ? 'confirmed' : ptt.knotState} />
          </button>
        ) : (
          <Knot size={64} state={receipt ? 'confirmed' : 'idle'} />
        )}
      </div>
      {receipt ? (
        <div class="frun__receipt">
          <b>Heard you.</b>
          <span>
            “{receipt.text}” → {receipt.line}
          </span>
        </div>
      ) : (
        <>
          <textarea
            class="frun__input frun__capture"
            value={captureText}
            onInput={(e) => setCaptureText((e.target as HTMLTextAreaElement).value)}
          />
          {askOffer && !askOffer.filed ? (
            <div class="frun__offer">
              <span>That is the office’s call.</span>
              {askOffer.token ? (
                <button type="button" class="frun__btn frun__btn--ghost" disabled={busy} onClick={() => void sendAsk()}>
                  Send it to the office
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
      <div class="frun__grow" />
      {receipt ? (
        <>
          <p class="frun__fine">That’s the whole app, really. Everything else is looking things up.</p>
          <button type="button" class="frun__btn" disabled={busy} onClick={() => void passStep('first_capture')}>
            Start work
          </button>
        </>
      ) : (
        <button
          type="button"
          class="frun__btn"
          disabled={busy || !captureText.trim() || Boolean(askOffer && !askOffer.token)}
          onClick={() => void capture()}
        >
          {busy ? 'One moment…' : 'Capture'}
        </button>
      )}
      <button type="button" class="frun__textlink" onClick={() => void passStep('first_capture')}>
        Do this later
      </button>
    </div>
  );
}
